import { IStoryContent } from '@opengov/stories-shared/types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';

import { IModalMessages, ISettings } from '../../../types';
import { CopyToTemplateParams, useCopyToTemplate } from '../../../stories/components/StoryView/hooks/useStoryMutations';

import { styles } from './CopyToTemplate.styles';

export type ICopyToTemplateProps = {
  open: boolean;
  onClose(): void;
  entityId: number;
  entitySubdomain: string;
  title?: string;
  description?: string;
  paddingTop: number;
  paddingBottom: number;
  storyContents: IStoryContent[];
  settings: ISettings;
  customCssCode?: string;
  messages: IModalMessages;
  userId: number;
}

export const CopyToTemplate = (props: ICopyToTemplateProps) => {
  const {
    open,
    onClose,
    entityId,
    entitySubdomain,
    userId,
    title: storyTitle,
    description: storyDescription,
    paddingTop,
    paddingBottom,
    storyContents,
    settings,
    customCssCode,
    messages,
  } = props;

  const [title, setTitle] = useState(storyTitle ?? '');
  const [description, setDescription] = useState(storyDescription ?? '');

  const { mutate: doCopyToTemplate } = useCopyToTemplate();

  const handleSubmit = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onClose();
    void doCopyToTemplate({
      entityId,
      entitySubdomain,
      userId,
      title,
      description,
      storyContents,
      messages: messages as unknown as CopyToTemplateParams['messages'],
      settings,
      paddingTop,
      paddingBottom,
      customCssCode,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="copy-to-template-dialog-title">
      <DialogTitle id="copy-to-template-dialog-title" sx={styles.dialogTitle}>
        {messages.copyToTemplate}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.formItem} data-test="story-template-title">
          <Box sx={styles.labelRow}>
            <Typography id="copy-to-template-title-label" variant="body2" sx={styles.formLabel}>
              {messages.title}
            </Typography>
            <Typography variant="caption" sx={styles.characterCounter}>
              {title.length}/60
            </Typography>
          </Box>
          <TextField
            size="small"
            sx={styles.textField}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 60, className: 'no-format', 'data-test': 'story-template-title-field', 'aria-labelledby': 'copy-to-template-title-label' }}
          />
        </Box>

        <Box sx={styles.formItem} data-test="story-template-description">
          <Box sx={styles.labelRow}>
            <Typography id="copy-to-template-description-label" variant="body2" sx={styles.formLabel}>
              {messages.description}
            </Typography>
            <Typography variant="caption" sx={styles.characterCounter}>
              {description.length}/150
            </Typography>
          </Box>
          <TextField
            size="small"
            multiline
            rows={2}
            sx={styles.descriptionTextField}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 150, className: 'no-format', 'data-test': 'story-template-description-field', 'aria-labelledby': 'copy-to-template-description-label' }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" onClick={onClose} data-test="story-cancel-copy-to-template">
          {messages.cancel}
        </Button>
        <Button variant="contained" color="primary" size="small" disabled={!title} onClick={handleSubmit}>
          {messages.copy}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
